import React, { useMemo, useRef } from 'react';
import { Layout } from 'fronton-react';
import { useStoreon } from 'storeon/react';
import {
  CreditBlock,
  Faq,
  InspGalleryRemote,
  OtherSection,
  ReviewSignUpButton,
  Section,
} from 'common/components';
import { useIsInViewport } from 'common/hooks';
import { REVIEWS_DATA } from 'common/components/kitchen';
import { KitchenGallery } from 'feature/kitchen-gallery';
import { COOKIES_STORE_NAME, selectIsInspGalleryABEnabled } from 'modules/cookies';
import { MODEL_JSON_STORE_NAME, selectInspirationGalleryData, useRegionIdSelector } from 'modules/model-json';
import { PromoBanner } from 'remotes/promo-banner';
import { State } from 'store/types';
import { KitchenBanner } from '../kitchen-banner';
import {
  KITCHEN_CDN_PATH,
  KITCHEN_ELEMENT_BLOCK,
  KITCHEN_LANDING_NAME,
  KITCHEN_SIGN_UP_HREF,
} from '../kitchen-constants';
import {
  Advices,
  AnimatedSignupBlock,
  Brands,
  ComplexCards,
  Header,
  MediaBlock,
  RelatedProducts,
  Reviews,
  SignupBlock,
  StageCards,
  StickyButton,
  UtpCards,
} from './components';
import {
  ADVICES,
  BRANDS,
  getStageBlockData,
  getUtpCards,
  MEDIA_BLOCK_BOTTOM,
  MEDIA_BLOCK_MIDDLE,
  MEDIA_BLOCK_TOP,
  RELATED_PRODUCTS,
  THREE_COMPLEX_CARDS,
  TWO_COMPLEX_CARDS,
} from './constants';

export const Kitchen = () => {
  const elRef = useRef<HTMLElement | null>(null);
  const regionId = useRegionIdSelector();
  const { cookies, modelJson } = useStoreon<State>(COOKIES_STORE_NAME, MODEL_JSON_STORE_NAME);
  const isInspGalleryABEnabled = useMemo(() => selectIsInspGalleryABEnabled(cookies), [cookies]);
  const inspirationGalleryData = useMemo(() => selectInspirationGalleryData(modelJson), [modelJson]);
  const isAnimatedBlockVisible = useIsInViewport(elRef, { threshold: 0 });

  const utpCards = useMemo(() => getUtpCards(regionId), [regionId]);

  const signUpHref = `${KITCHEN_SIGN_UP_HREF}${regionId}`;

  return (
    <>
      <Layout>
        <Header
          title="Кухни для жизни"
          video={{
            previewImg: `${KITCHEN_CDN_PATH}/new-kitchen/header-b/preview-ld.jpg/preview-ld.jpg`,
            videoSrc: 'https://cdn.lemanapro.ru/lmru/image/upload/video/fudzi/mf-landings-web%252Fvideo%252Fkitchen-ld.mp4/kitchen-ld.mp4',
            videoSrcM: 'https://cdn.lemanapro.ru/lmru/image/upload/video/fudzi/mf-landings-web%252Fvideo%252Fkitchen-m.mp4/kitchen-m.mp4',
          }}
        />
        <UtpCards elementBlock={KITCHEN_ELEMENT_BLOCK} cards={utpCards} />

        <StickyButton
          isVisible={!isAnimatedBlockVisible}
          elementBlock={KITCHEN_ELEMENT_BLOCK}
          label="Записаться на проектирование"
          btnProps={{
            href: signUpHref,
            target: '_blank',
            title: 'Запись на бесплатное проектирование кухни',
          }}
        />

        {isInspGalleryABEnabled ? (
          <Section>
            <InspGalleryRemote
              regionId={regionId}
              inspirationGalleryData={inspirationGalleryData}
              analyticsName="landingProektKuhni / ggr"
            />
          </Section>
        ) : (
          <KitchenGallery elementBlock={KITCHEN_ELEMENT_BLOCK} isRuRegion />
        )}

        <StageCards elementBlock={KITCHEN_ELEMENT_BLOCK} {...getStageBlockData(regionId)} />
        <PromoBanner elementBlock={KITCHEN_ELEMENT_BLOCK} />
        <ComplexCards
          elementBlock={KITCHEN_ELEMENT_BLOCK}
          cards={THREE_COMPLEX_CARDS}
        />
        <MediaBlock elementBlock={KITCHEN_ELEMENT_BLOCK} cards={MEDIA_BLOCK_TOP} />
        <MediaBlock elementBlock={KITCHEN_ELEMENT_BLOCK} cards={MEDIA_BLOCK_MIDDLE} />
        <Brands
          elementBlock={KITCHEN_ELEMENT_BLOCK}
          brands={BRANDS}
          title="Бренды, которым доверяют"
        />
        <RelatedProducts
          elementBlock={KITCHEN_ELEMENT_BLOCK}
          title="Всё готово, чтобы готовить"
          cards={RELATED_PRODUCTS}
        />
        <ComplexCards
          elementBlock={KITCHEN_ELEMENT_BLOCK}
          cards={TWO_COMPLEX_CARDS}
        />
        <MediaBlock elementBlock={KITCHEN_ELEMENT_BLOCK} cards={MEDIA_BLOCK_BOTTOM} />
        <SignupBlock elementBlock={KITCHEN_ELEMENT_BLOCK} signUpHref={signUpHref} />
        <KitchenBanner elementBlock={KITCHEN_ELEMENT_BLOCK} />
        <CreditBlock />
        <Reviews
          elementBlock={KITCHEN_ELEMENT_BLOCK}
          title="1 млн проектов за 10 лет — и каждый для жизни"
          reviews={REVIEWS_DATA}
          reviewModalButton={
            <ReviewSignUpButton elementBlcok={KITCHEN_ELEMENT_BLOCK} signUpHref={signUpHref} />
          }
        />
      </Layout>
      <AnimatedSignupBlock
        elementBlock={KITCHEN_ELEMENT_BLOCK}
        refCb={(el: HTMLElement | null) => {
          elRef.current = el;
        }}
        signUpHref={signUpHref}
      />
      <Layout>
        <Advices
          elementBlock={KITCHEN_ELEMENT_BLOCK}
          advices={ADVICES}
          title="Кухня своими силами — идеи и советы"
        />
        {modelJson?.model?.faq && (
          <Faq
            elementBlock={KITCHEN_ELEMENT_BLOCK}
            {...modelJson.model.faq}
          />
        )}
      </Layout>
      <OtherSection
        elementBlock={`${KITCHEN_ELEMENT_BLOCK} / other`}
        landingName={KITCHEN_LANDING_NAME}
        promotionName={`${KITCHEN_ELEMENT_BLOCK}_bottom`}
        title="Единый стиль для всего дома"
        variant="B"
      />
    </>
  )
};
